import { z } from "zod";
//#region src/code-unit-string.ts
/**
* A zod string whose length caps count UTF-16 code units (`String.prototype.length`), the unit the
* platform's string caps state: the audit text caps, the Ajv parity doors, the DB columns and the
* byte pre-filters. zod 4.6 counts Unicode code points in `.max`, `.min` and `.length`, and has no
* option to count units, so a contract that states a code-unit cap builds its string here.
*
* This module imports only zod, so a contract of any domain can use it and no module cycle can
* pass through it.
*/
/** The run condition of every check here: a string value. Another type already has its type issue. */
const whenString = (payload) => typeof payload.value === "string";
/**
* The code-unit twin of zod's `$ZodCheckMaxLength`. It pushes the same issue (`too_big`,
* `origin: 'string'`, `maximum`, `inclusive: true`) and writes `bag.maximum`, so the JSON Schema
* export keeps `maxLength`. Its `check` name is unknown to `z.compile`, which then keeps the
* runtime parser: the compiled length test counts code points.
*/
const CodeUnitCheckMaxLength = z.core.$constructor("CodeUnitCheckMaxLength", (inst, def) => {
	z.core.$ZodCheck.init(inst, def);
	def.when ??= whenString;
	inst._zod.onattach.push((schema) => {
		const bag = schema._zod.bag;
		if (typeof bag.maximum !== "number" || def.maximum < bag.maximum) bag.maximum = def.maximum;
	});
	inst._zod.check = (payload) => {
		const input = payload.value;
		if (input.length <= def.maximum) return;
		payload.issues.push({
			origin: "string",
			code: "too_big",
			maximum: def.maximum,
			inclusive: true,
			input,
			inst,
			continue: !def.abort
		});
	};
});
/** The code-unit twin of zod's `$ZodCheckMinLength`: `too_small` with `minimum`, and `bag.minimum`. */
const CodeUnitCheckMinLength = z.core.$constructor("CodeUnitCheckMinLength", (inst, def) => {
	z.core.$ZodCheck.init(inst, def);
	def.when ??= whenString;
	inst._zod.onattach.push((schema) => {
		const bag = schema._zod.bag;
		if (typeof bag.minimum !== "number" || def.minimum > bag.minimum) bag.minimum = def.minimum;
	});
	inst._zod.check = (payload) => {
		const input = payload.value;
		if (input.length >= def.minimum) return;
		payload.issues.push({
			origin: "string",
			code: "too_small",
			minimum: def.minimum,
			inclusive: true,
			input,
			inst,
			continue: !def.abort
		});
	};
});
/**
* The code-unit twin of zod's `$ZodCheckLengthEquals`: `too_big` or `too_small` with `exact: true`,
* and `bag.length` set to the length. `bag.minimum` and `bag.maximum` take the length unless an
* earlier bound is tighter, the same rule as the `.min` and `.max` checks.
*/
const CodeUnitCheckLengthEquals = z.core.$constructor("CodeUnitCheckLengthEquals", (inst, def) => {
	z.core.$ZodCheck.init(inst, def);
	def.when ??= whenString;
	inst._zod.onattach.push((schema) => {
		const bag = schema._zod.bag;
		if (typeof bag.minimum !== "number" || def.length > bag.minimum) bag.minimum = def.length;
		if (typeof bag.maximum !== "number" || def.length < bag.maximum) bag.maximum = def.length;
		bag.length = def.length;
	});
	inst._zod.check = (payload) => {
		const input = payload.value;
		if (input.length === def.length) return;
		const bound = input.length > def.length ? {
			code: "too_big",
			maximum: def.length
		} : {
			code: "too_small",
			minimum: def.length
		};
		payload.issues.push({
			origin: "string",
			...bound,
			inclusive: true,
			exact: true,
			input,
			inst,
			continue: !def.abort
		});
	};
});
/**
* `ZodString` whose `.max`, `.min` and `.length` install the code-unit checks above. zod clones a
* schema through its constructor on every chained call, so the overrides survive `.trim()`,
* `.regex()`, `.optional()` and `.describe()`.
*/
const CodeUnitString = z.core.$constructor("CodeUnitString", (inst, def) => {
	z.ZodString.init(inst, def);
	inst.max = (maximum, params) => inst.check(new CodeUnitCheckMaxLength({
		...z.core.util.normalizeParams(params),
		check: "code_unit_max_length",
		maximum
	}));
	inst.min = (minimum, params) => inst.check(new CodeUnitCheckMinLength({
		...z.core.util.normalizeParams(params),
		check: "code_unit_min_length",
		minimum
	}));
	inst.length = (length, params) => inst.check(new CodeUnitCheckLengthEquals({
		...z.core.util.normalizeParams(params),
		check: "code_unit_length_equals",
		length
	}));
});
/**
* The string builder for a contract whose length caps count UTF-16 code units.
*
* Callers: contract fields whose cap the platform states in code units, bytes, a DB column or an
* Ajv parity door. The modules of this package that call it:
* - `audit-envelope`, `audit-archive-layout`, `cp-audit/ingest` and `cp-audit/read-details`;
* - `dp-logs-query`, `app-logs`, `app-source` and `mail/app-mail`;
* - `compute-resolution`, `scaling-event`, `library/library-rights` and `machine-principal`;
* - `kernel/kernel-wire-internal`, `kernel/second-factor` and `single-line-text`.
* Outside this package, cp-auth-api's `audit-ingest.schema.ts` calls it through the root export.
*
* Input: `params`, the same optional `string | $ZodStringParams` that `z.string(params)` takes.
* Output: a `ZodString` that behaves as `z.string(params)` in every way but three:
* - `.max(n)` refuses a string whose `length` is over `n` with zod's `too_big` issue
*   (`origin: 'string'`, `maximum: n`, `inclusive: true`);
* - `.min(n)` refuses a string whose `length` is under `n` with zod's `too_small` issue
*   (`minimum: n`);
* - `.length(n)` refuses any other `length` with `too_big` or `too_small` and `exact: true`.
*
* So 65 astral characters (130 code units) fail `.max(128)`, where `z.string().max(128)` in zod
* 4.6 counts 65 code points and passes them. The JSON Schema export writes `maxLength` and
* `minLength` as zod does. Under `z.compile` a schema that holds one of these checks keeps zod's
* runtime parser, so the rule holds there too. Pure; builds a schema and never throws.
*/
function codeUnitString(params) {
	return z.core._string(CodeUnitString, params);
}
//#endregion
export { codeUnitString };

//# sourceMappingURL=code-unit-string.js.map